// Cross-section maths for per-lane band rendering: ONE centreline stroke, thickened
// with the lane count and cut into one coloured band per lane, the bands separated by
// thin slivers of a casing drawn underneath. Parallel offset lines were tried first
// and read as several roads rather than one.
//
// Pure functions on plain data, kept apart from the OL styling in lanes.js so the
// geometry maths can be reasoned about on its own.
//
// Every width and offset here is in SCREEN PIXELS. Stroke widths can be handed to OL
// as they are, but the offsets move geometry: multiply by the map resolution first,
// or the lanes drift apart as you zoom.

// Separators are a FIXED pixel width, not a share of the stroke: a proportional split
// goes sub-pixel and stops rendering. `totalW` is therefore DERIVED from its parts, so
// the cross-section identity holds by construction. See context.md for the numbers.
import { splitLaneList } from "./laneLists.mjs";

const INNER_SEP_PX = 1;
// Slightly wider than an inner separator so the ribbon reads as outlined rather
// than as N loose bands, and so its edge doubles as the stroke's outline.
const OUTER_SEP_PX = 1.25;
// What a band gets when there is room. Below ~1.5px a band's colour stops being
// identifiable, which is the whole point of the layer, so that is the floor.
const BAND_TARGET_PX = 2.6;
const BAND_MIN_PX = 1.5;
// Bands shrink (never below BAND_MIN_PX) to keep a wide road inside this budget.
// Past it the stroke starts hiding the network it is drawn on. Lane counts run 2-3
// typically and 7 at the extreme, so the budget only binds from about 5 lanes up.
const TOTAL_BUDGET_PX = 18;
// Most bands one way can render. Past this the ribbon would be wider than the roads
// around it, so the layer declines: normalizeLaneCount returns 0 and the way falls back
// to a single stroke rather than being drawn with the wrong number of lanes.
//
// Truncating instead would be worse than refusing. Offsets are computed FROM the count,
// so clamping a 10-slot list to 8 doesn't just drop two lanes, it re-lays the surviving
// eight at the wrong lateral positions — a confident, plausible, wrong picture. A
// hairline plus the info panel is honest. Slot counts come from map data and can be
// malformed, so this doubles as the guard against a value that isn't a lane list at all.
const MAX_RENDERABLE_LANES = 8;

// The per-lane pipe lists this module knows about. Values are physical
// left-to-right (digitization) order — that guarantee from the OSM normalization is
// what makes a signed lateral offset meaningful at all.
const LANE_LIST_TAGS = [
  "rm_lanesDirection",
  "rm_vehicleAccessMotorcar",
  "rm_vehicleAccessHgv",
  "rm_vehicleAccessBus",
  "rm_vehicleAccessTaxi",
  "rm_hovLanes",
  // The #Conditional siblings are pipe lists like any other: the ` @ (…)` condition
  // lives INSIDE a slot and a time domain never contains `|`, so the split is safe.
  "rm_hovLanesConditional",
  "rm_hovMinimumLanesConditional",
];
// Tier 2 of the lane-count precedence; `rm_lanesDirection` is tier 1 and asked first.
const COUNTED_LANE_LIST_TAGS = [
  "rm_vehicleAccessMotorcar",
  "rm_vehicleAccessHgv",
  "rm_vehicleAccessBus",
  "rm_vehicleAccessTaxi",
  "rm_hovLanes",
  "rm_hovLanesConditional",
  "rm_hovMinimumLanesConditional",
];

/**
 * How many lanes to draw, by the documented precedence: the slot count of
 * `lanes:direction`, else the widest of the other per-lane lists, else the integer
 * `lanes=N`.
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @returns {number} 0 when the feature says nothing about lanes
 */
export function laneCount(tags) {
  if (!tags) return 0;
  const directionSlots = splitLaneList(tags.rm_lanesDirection).length;
  if (directionSlots > 0) return normalizeLaneCount(directionSlots);
  const listedSlots = Math.max(
    0,
    ...COUNTED_LANE_LIST_TAGS.map((name) => splitLaneList(tags[name]).length),
  );
  if (listedSlots > 0) return normalizeLaneCount(listedSlots);
  return normalizeLaneCount(Number.parseInt(tags.rm_lanesTotal, 10));
}

/**
 * The one place a lane count is normalised. `bandGeometry` and `laneSlots` used to
 * clamp differently — `Number.isFinite(n) ? clamp(…, 1, MAX) : 1` against
 * `Math.max(0, Math.floor(n))` — so for n of 0, NaN, null, `"3"` or 1000 they
 * disagreed on how many lanes there were. Anything zipping `offsets` against `slots`
 * then read `undefined` slots or dropped bands off the end.
 * @param {unknown} n
 * @returns {number} an integer in [0, MAX_LANE_COUNT]
 */
function normalizeLaneCount(n) {
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num) || num <= 0) return 0;
  const lanes = Math.floor(num);
  return lanes > MAX_RENDERABLE_LANES ? 0 : lanes;
}

/**
 * Transpose the pipe lists into `n` per-lane scalar tag sets. Each slot carries the
 * same key names as the tag set it came from, so a per-lane classifier reads exactly
 * like a per-way one. Padding a short list and truncating a long one both fall out
 * of the index lookup: a missing slot is blank, which is the "nothing said about
 * this lane" value the encoding already uses.
 * @param {Record<string, string|undefined>} tags
 * @param {number} n
 * @returns {Record<string, string>[]} length n, lane 0 = leftmost
 */
export function laneSlots(tags, n) {
  const lists = {};
  LANE_LIST_TAGS.forEach((name) => {
    lists[name] = splitLaneList(tags ? tags[name] : undefined);
  });
  const slots = [];
  const lanes = normalizeLaneCount(n);
  for (let i = 0; i < lanes; i++) {
    const slot = {};
    LANE_LIST_TAGS.forEach((name) => {
      slot[name] = lists[name][i] ?? "";
    });
    slots.push(slot);
  }
  return slots;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * The cross-section of an `n`-lane stroke, in screen pixels. The separators are the
 * slivers of casing the bands don't cover, which is why there is a casing at all: by
 * construction `2*outerW + n*band + (n-1)*innerW === totalW`, so no rounding gap can
 * open up, it is n+1 strokes instead of 2n+1, and adjacent separators can't z-fight.
 *
 * `offsets[i]` is lane i's signed distance from the centreline, `+` = right of
 * travel. n=1 centres on the line, n=2 comes out symmetric.
 * @param {number} n
 * @returns {{totalW: number, band: number, innerW: number, outerW: number, offsets: number[]}}
 */
export function bandGeometry(n) {
  // Normalised through the shared helper so this agrees with laneSlots(). A NaN used
  // to propagate into every width and leave `offsets` empty, breaking the length-n
  // contract callers index into. A count of 0 means "no lane info" and the caller is
  // expected to have taken the hairline path, but a cross-section still needs at least
  // one band to be well-defined, so it floors at 1 here.
  const lanes = Math.max(1, normalizeLaneCount(n));
  const innerW = INNER_SEP_PX;
  const outerW = OUTER_SEP_PX;
  // Separators come first, out of the budget; the bands divide what is left, down to
  // BAND_MIN_PX. Beyond that the budget gives, not the bands — a 7-lane road ends up
  // slightly over TOTAL_BUDGET_PX rather than illegible.
  const separators = 2 * outerW + (lanes - 1) * innerW;
  const band = clamp(
    (TOTAL_BUDGET_PX - separators) / lanes,
    BAND_MIN_PX,
    BAND_TARGET_PX,
  );
  const totalW = lanes * band + separators;
  const offsets = [];
  for (let i = 0; i < lanes; i++) {
    offsets.push(outerW + i * (band + innerW) + band / 2 - totalW / 2);
  }
  return { totalW, band, innerW, outerW, offsets };
}

/**
 * Shift every vertex perpendicular to its local heading by `distance` map units,
 * signed with `+` = right of travel.
 *
 * Written here rather than reusing `polyline.mjs`'s offsetPolyline, which returns
 * <3-vertex lines UNSHIFTED to protect the CaJ hover tangent probe: road-model
 * geometry is full of 2-vertex straight ways, and on those every lane band would
 * silently stack on the centreline. The per-vertex heading is the sum of the
 * adjacent segments' unit vectors — summing vectors rather than averaging atan2
 * angles, which would fold ±180° back to 0 and flip the offset on a way that nearly
 * doubles back. Returns a new array; the input is not mutated.
 * @param {number[][]} coords
 * @param {number} distance
 * @returns {number[][]}
 */
export function offsetCoordinates(coords, distance) {
  const n = coords.length;
  if (n < 2 || !distance) return coords.map((c) => [c[0], c[1]]);
  const unit = (from, to) => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const len = Math.hypot(dx, dy);
    return len === 0 ? [0, 0] : [dx / len, dy / len];
  };
  // A closed way — a roundabout is the common case, and roundabouts carry lane counts
  // — has its first and last vertex at the same point. Treating those as line ends
  // gives each a one-sided heading, so the two offset copies land in different places
  // and the ribbon opens a wedge at the join. Wrapping the neighbour lookup round makes
  // both compute the same heading, closing it.
  const closed =
    n > 2 &&
    coords[0][0] === coords[n - 1][0] &&
    coords[0][1] === coords[n - 1][1];
  // Walk to the nearest DISTINCT vertex, not the immediate neighbour. Duplicated
  // points are common in map data, and at the FIRST or LAST vertex there is no other
  // side to fall back on — so a duplicated endpoint was left sitting on the centreline
  // while the rest of its band moved off it, which on a 3-lane way made all three
  // bands converge to a single point: a visible N-way fan at the way's end.
  const same = (a, b) => a[0] === b[0] && a[1] === b[1];
  const prevOf = (i) => {
    for (let k = i - 1; k >= 0; k--) if (!same(coords[k], coords[i])) return k;
    if (!closed) return -1;
    for (let k = n - 2; k > i; k--) if (!same(coords[k], coords[i])) return k;
    return -1;
  };
  const nextOf = (i) => {
    for (let k = i + 1; k < n; k++) if (!same(coords[k], coords[i])) return k;
    if (!closed) return -1;
    for (let k = 1; k < i; k++) if (!same(coords[k], coords[i])) return k;
    return -1;
  };
  return coords.map((c, i) => {
    const p = prevOf(i);
    const q = nextOf(i);
    const back = p >= 0 ? unit(coords[p], c) : [0, 0];
    const forward = q >= 0 ? unit(c, coords[q]) : [0, 0];
    let hx = back[0] + forward[0];
    let hy = back[1] + forward[1];
    let len = Math.hypot(hx, hy);
    if (len === 0) {
      // The two segments cancelled: a duplicate vertex, or a 180° spike where the way
      // doubles straight back on itself. Leaving this vertex un-offset would pin it to
      // the centreline while its neighbours moved, kinking the band into a visible
      // spike — so fall back to whichever single segment exists.
      const fallback = forward[0] || forward[1] ? forward : back;
      hx = fallback[0];
      hy = fallback[1];
      len = Math.hypot(hx, hy);
      // Genuinely no direction anywhere (every neighbour is the same point).
      if (len === 0) return [c[0], c[1]];
    }
    hx /= len;
    hy /= len;
    // Right-of-travel normal of heading (hx, hy) is (hy, -hx) in a y-up map.
    return [c[0] + hy * distance, c[1] - hx * distance];
  });
}

/**
 * Extend a polyline by `distance` map units at BOTH ends, along the direction of the
 * first and last segments.
 *
 * Used to overshoot the lane casing past its node so adjacent ways' casings overlap and
 * the junction reads as joined rather than as two ribbons stopping short of each other.
 * A FIXED overshoot rather than OL's round cap, which overshoots by half the stroke
 * width — that scaled with the lane count, so the wider the ribbon the bigger the blob.
 *
 * This is a cartographic joint, not a topological one: which lane of one way feeds which
 * lane of the next is the Lane Connectivity relation, which these layers don't carry.
 * @param {number[][]} coords
 * @param {number} distance
 * @returns {number[][]} a new array; the input is not mutated
 */
export function extendEnds(coords, distance) {
  const n = coords.length;
  if (n < 2 || !distance) return coords.map((c) => [c[0], c[1]]);
  const step = (from, to) => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const len = Math.hypot(dx, dy);
    return len === 0 ? null : [(dx / len) * distance, (dy / len) * distance];
  };
  const out = coords.map((c) => [c[0], c[1]]);
  // Walk to the first DISTINCT neighbour: a duplicated endpoint gives no direction.
  let head = null;
  for (let k = 1; k < n && !head; k++) head = step(coords[k], coords[0]);
  let tail = null;
  for (let k = n - 2; k >= 0 && !tail; k--)
    tail = step(coords[k], coords[n - 1]);
  if (head) out[0] = [coords[0][0] + head[0], coords[0][1] + head[1]];
  if (tail)
    out[n - 1] = [coords[n - 1][0] + tail[0], coords[n - 1][1] + tail[1]];
  return out;
}
